import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiBell,
  FiSearch,
  FiPaperclip,
  FiUploadCloud,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { noticeAPI } from "../../services/api";
import { formatDate, getFileExtension } from "../../utils/helpers";
import { triggerRefresh } from "../../hooks/useDataRefresh";
import AdminLayout from "../../components/layout/AdminLayout";
import Modal from "../../components/common/Modal";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import AttachmentList from "../../components/common/AttachmentList";
import FileViewer from "../../components/common/FileViewer";

// Keep in sync with NOTICE_ATTACHMENT_EXTENSIONS in Backend/middleware/upload.js
const ATTACHMENT_EXTENSIONS = [
  "jpg", "jpeg", "png", "gif", "webp", "bmp",
  "pdf",
  "doc", "docx", "xls", "xlsx", "ppt", "pptx",
  "odt", "ods", "odp", "rtf", "txt", "csv",
  "mp4", "webm", "mov", "mp3", "wav", "m4a",
  "zip", "rar", "7z",
];
const ATTACHMENT_ACCEPT = ATTACHMENT_EXTENSIONS.map((ext) => `.${ext}`).join(",");
const MAX_FILE_MB = 10;
const MAX_FILES_PER_SAVE = 5;
const MAX_UPLOAD_TOTAL_MB = 20; // nginx client_max_body_size

const CATEGORIES = [
  "General",
  "Academic",
  "Sports",
  "Events",
  "Urgent",
  "Holiday",
  "Exam",
];

const emptyForm = {
  title: "",
  content: "",
  category: "General",
  publishDate: "",
  expiryDate: "",
  isPublished: true,
};

const AdminNotices = () => {
  const [notices, setNotices] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [keptAttachments, setKeptAttachments] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const [newFiles, setNewFiles] = useState([]); // { file, url (object URL), name, size, mimeType }
  const [viewer, setViewer] = useState(null); // { files, index, title }
  const newFilesRef = useRef(newFiles);
  newFilesRef.current = newFiles;

  const releaseNewFiles = () => {
    newFilesRef.current.forEach((f) => URL.revokeObjectURL(f.url));
    setNewFiles([]);
  };

  // Free local preview URLs when leaving the page
  useEffect(
    () => () => newFilesRef.current.forEach((f) => URL.revokeObjectURL(f.url)),
    [],
  );

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        const response = await noticeAPI.getAdmin();
        const data = response.data?.data?.notices || [];
        setNotices(data);
        setFiltered(data);
      } catch (err) {
        toast.error("Failed to load notices");
      } finally {
        setLoading(false);
      }
    };
    fetchNotices();
  }, []);

  useEffect(() => {
    const result = search.trim()
      ? notices.filter((n) =>
          n.title.toLowerCase().includes(search.toLowerCase()),
        )
      : notices;
    setFiltered(result);
  }, [search, notices]);

  const openCreate = () => {
    setEditItem(null);
    setForm(emptyForm);
    setKeptAttachments([]);
    setRemovedAttachmentIds([]);
    releaseNewFiles();
    setModal(true);
  };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      title: item.title,
      content: item.content,
      category: item.category,
      publishDate: item.publishDate?.slice(0, 10) || "",
      expiryDate: item.expiryDate?.slice(0, 10) || "",
      isPublished: item.isPublished,
    });
    setKeptAttachments(item.attachments || []);
    setRemovedAttachmentIds([]);
    releaseNewFiles();
    setModal(true);
  };
  const closeModal = () => {
    setModal(false);
    releaseNewFiles();
  };

  // Attachments shown in the form: saved ones first, then files picked but not yet uploaded
  const formAttachments = [...keptAttachments, ...newFiles];

  const handleFilesSelected = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = ""; // allow picking the same file again after removing it
    const accepted = [];
    for (const file of picked) {
      if (!ATTACHMENT_EXTENSIONS.includes(getFileExtension(file))) {
        toast.error(`"${file.name}" is not a supported file type`);
      } else if (file.size > MAX_FILE_MB * 1024 * 1024) {
        toast.error(`"${file.name}" is larger than ${MAX_FILE_MB}MB`);
      } else {
        accepted.push(file);
      }
    }
    const room = MAX_FILES_PER_SAVE - newFiles.length;
    if (accepted.length > room) {
      toast.error(`You can add up to ${MAX_FILES_PER_SAVE} files per save`);
    }
    const additions = accepted.slice(0, Math.max(room, 0)).map((file) => ({
      file,
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      mimeType: file.type,
    }));
    if (additions.length) setNewFiles((prev) => [...prev, ...additions]);
  };

  const removeFormAttachment = (index) => {
    if (index < keptAttachments.length) {
      const removed = keptAttachments[index];
      setKeptAttachments((prev) => prev.filter((_, i) => i !== index));
      setRemovedAttachmentIds((prev) => [...prev, removed._id]);
    } else {
      const removed = newFiles[index - keptAttachments.length];
      URL.revokeObjectURL(removed.url);
      setNewFiles((prev) => prev.filter((f) => f !== removed));
    }
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    const uploadBytes = newFiles.reduce((sum, f) => sum + f.size, 0);
    if (uploadBytes > MAX_UPLOAD_TOTAL_MB * 1024 * 1024) {
      toast.error(
        `New files add up to more than ${MAX_UPLOAD_TOTAL_MB}MB. Save with fewer files, then edit the notice to add the rest.`,
      );
      return;
    }

    const payload = new FormData();
    Object.entries(form).forEach(([key, value]) => payload.append(key, value));
    newFiles.forEach((f) => payload.append("attachments", f.file));
    if (removedAttachmentIds.length) {
      payload.append("removeAttachments", JSON.stringify(removedAttachmentIds));
    }

    setSaving(true);
    try {
      if (editItem) {
        const response = await noticeAPI.update(editItem._id, payload);
        const updated = response.data?.data?.notice || { ...editItem, ...form };
        setNotices((prev) =>
          prev.map((n) => (n._id === editItem._id ? updated : n)),
        );
        toast.success("Notice updated");
        triggerRefresh("notices"); // Update all notices sections
      } else {
        const response = await noticeAPI.create(payload);
        const newItem = response.data?.data?.notice;
        if (newItem) setNotices((prev) => [newItem, ...prev]);
        toast.success("Notice created");
        triggerRefresh("notices"); // Update all notices sections
      }
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save notice");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await noticeAPI.togglePublish(id);
      setNotices((prev) =>
        prev.map((n) =>
          n._id === id ? { ...n, isPublished: !n.isPublished } : n,
        ),
      );
      toast.success("Notice status updated");
      triggerRefresh("notices"); // Update all notices sections
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    try {
      await noticeAPI.delete(id);
      setNotices((prev) => prev.filter((n) => n._id !== id));
      setDeleteConfirm(null);
      toast.success("Notice deleted");
      triggerRefresh("notices"); // Update all notices sections
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete notice");
    }
  };

  const categoryColors = {
    Academic: "bg-blue-100 text-blue-700",
    Holiday: "bg-green-100 text-green-700",
    Exam: "bg-purple-100 text-purple-700",
    Event: "bg-orange-100 text-orange-700",
    General: "bg-gray-100 text-gray-700",
    Urgent: "bg-red-100 text-red-700",
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-serif font-bold text-primary-500">
            Notices Management
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Create and manage school notices
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 transition-colors text-sm"
        >
          <FiPlus className="w-4 h-4" />
          New Notice
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search notices..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold">
                    Title
                  </th>
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold hidden sm:table-cell">
                    Category
                  </th>
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold hidden md:table-cell">
                    Date
                  </th>
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold">
                    Status
                  </th>
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((notice, idx) => (
                  <motion.tr
                    key={notice._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.05 }}
                    className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-5 py-4">
                      <div className="font-medium text-gray-800 max-w-xs truncate">
                        {notice.title}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5 max-w-xs truncate">
                        {notice.content}
                      </div>
                      {notice.attachments?.length > 0 && (
                        <button
                          type="button"
                          onClick={() =>
                            setViewer({
                              files: notice.attachments,
                              index: 0,
                              title: notice.title,
                            })
                          }
                          className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-50 text-primary-600 text-xs font-medium hover:bg-primary-100 transition-colors"
                          title="View attachments"
                        >
                          <FiPaperclip className="w-3 h-3" />
                          {notice.attachments.length}{" "}
                          {notice.attachments.length === 1 ? "file" : "files"}
                        </button>
                      )}
                    </td>
                    <td className="px-5 py-4 hidden sm:table-cell">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${categoryColors[notice.category] || categoryColors.General}`}
                      >
                        {notice.category}
                      </span>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell text-gray-500 text-xs">
                      {formatDate(notice.publishDate)}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${notice.isPublished ? "text-green-700 bg-green-100 border-green-200" : "text-gray-700 bg-gray-100 border-gray-200"}`}
                      >
                        {notice.isPublished ? "Published" : "Draft"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => openEdit(notice)}
                          className="p-1.5 text-primary-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <FiEdit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleToggle(notice._id)}
                          className={`p-1.5 rounded-lg transition-colors ${notice.isPublished ? "text-orange-400 hover:text-orange-600 hover:bg-orange-50" : "text-green-400 hover:text-green-600 hover:bg-green-50"}`}
                          title={notice.isPublished ? "Unpublish" : "Publish"}
                        >
                          {notice.isPublished ? (
                            <FiEyeOff className="w-3.5 h-3.5" />
                          ) : (
                            <FiEye className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(notice._id)}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-12">
              <FiBell className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No notices found</p>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modal}
        onClose={closeModal}
        title={editItem ? "Edit Notice" : "Create Notice"}
        size="lg"
        footer={
          <>
            <button
              onClick={closeModal}
              className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-xl text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-70"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : null}
              {editItem ? "Update" : "Create"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              value={form.title}
              onChange={(e) =>
                setForm((p) => ({ ...p, title: e.target.value }))
              }
              placeholder="Notice title"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={form.category}
              onChange={(e) =>
                setForm((p) => ({ ...p, category: e.target.value }))
              }
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none bg-white"
            >
              {CATEGORIES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Publish Date
              </label>
              <input
                type="date"
                value={form.publishDate}
                onChange={(e) =>
                  setForm((p) => ({ ...p, publishDate: e.target.value }))
                }
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiry Date
              </label>
              <input
                type="date"
                value={form.expiryDate}
                onChange={(e) =>
                  setForm((p) => ({ ...p, expiryDate: e.target.value }))
                }
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content *
            </label>
            <textarea
              value={form.content}
              onChange={(e) =>
                setForm((p) => ({ ...p, content: e.target.value }))
              }
              rows={5}
              placeholder="Notice content..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Attachments
            </label>
            <p className="text-xs text-gray-400 mb-3">
              Images, PDF, Word, Excel, PowerPoint, text, audio/video or ZIP —
              up to {MAX_FILE_MB}MB each, {MAX_FILES_PER_SAVE} new files per
              save. Click a file to preview it.
            </p>
            {formAttachments.length > 0 && (
              <div className="mb-3">
                <AttachmentList
                  attachments={formAttachments}
                  onOpen={(index) =>
                    setViewer({
                      files: formAttachments,
                      index,
                      title: form.title,
                    })
                  }
                  onRemove={removeFormAttachment}
                />
              </div>
            )}
            <label className="flex flex-col items-center justify-center gap-1.5 px-4 py-5 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 cursor-pointer hover:border-primary-300 hover:bg-primary-50/40 transition-colors">
              <FiUploadCloud className="w-6 h-6 text-primary-400" />
              <span>
                <span className="font-medium text-primary-500">
                  Click to choose files
                </span>{" "}
                to attach
              </span>
              <input
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                onChange={handleFilesSelected}
                className="sr-only"
              />
            </label>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isPublished}
              onChange={(e) =>
                setForm((p) => ({ ...p, isPublished: e.target.checked }))
              }
              className="w-4 h-4 rounded accent-primary-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Publish immediately (visible on website)
            </span>
          </label>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        size="sm"
        footer={
          <>
            <button
              onClick={() => setDeleteConfirm(null)}
              className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => handleDelete(deleteConfirm)}
              className="px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600"
            >
              Delete
            </button>
          </>
        }
      >
        <p className="text-gray-600 text-sm">
          Are you sure you want to delete this notice and its attachments?
          This action cannot be undone.
        </p>
      </Modal>

      <FileViewer
        files={viewer?.files}
        startIndex={viewer?.index}
        title={viewer?.title}
        onClose={() => setViewer(null)}
      />
    </AdminLayout>
  );
};

export default AdminNotices;
